import React, { memo, useContext, useState } from 'react';
import isEqual from 'lodash.isequal';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { Icon, TextLarge, TextRegular, TIcon, Title4 } from '@hz-design-system/web-ui';
import { ImageSize } from '@ecg-marktplaats/media-util-js-lib';
import { ListingAttribute, ListingSellerName } from '@/client/components/Listings/atoms';
import { imageFormatter, imageSizeFormatter } from '@/client/utils/formatters';
import {
  PRICE_TYPE_ATTRIBUTE_VALUE_KEY_FR,
  PRICE_TYPE_ATTRIBUTE_VALUE_KEY_NL,
} from '@/constants/attributes/nonStandards';
import formatAttributeBasedOnKey from '@/client/utils/formatters/formatBasedOnKey';
import ListingVipLink from '@/client/components/Listings/atoms/ListingVipLink';
import ListingTrustIndicator from '@/client/components/Listings/atoms/ListingTrustIndicator';
import SellerExternalWebsite from '@/client/components/Listings/molecules/SellerExternalWebsite';
import PRICE_TYPE from '@/client/constants/priceTypes';
import EnvironmentContext from '@/client/contexts/EnvironmentContext';
import experiments from '@/utils/labs/experiments';

import { Attribute } from '@/client/types/Listings';
import { TImage } from '@/client/types/Images';
// new implementation of the seller other listings component from DS
import { ListingDetailsButton, ListingFigure, ListingOtherSeller } from '@/client/components/ListingsNew/components';
import trackEvent from '@/client/utils/analytics/trackEvent';
import trackingKeys from '@/client/constants/analytics/trackingKeys';

import Classes from './ListingListViewContentCars.module.scss';

const SPACE_SEPARATOR = ' ';
const MILEAGE_ATTRIBUTE_KEY = 'mileage';
const CAR_PASS_ATTRIBUTE_KEY = 'carPassUrl';
const OPTIONS_ATTRIBUTE_KEY = 'options';
const FUEL_ATTRIBUTE_KEY = 'fuel';
const CAR_ATTRIBUTES = ['constructionYear', MILEAGE_ATTRIBUTE_KEY, FUEL_ATTRIBUTE_KEY, 'transmission', 'body'];
const CAR_EV_ATTRIBUTES = ['constructionYear', MILEAGE_ATTRIBUTE_KEY, FUEL_ATTRIBUTE_KEY, 'range', 'body'];
const DISPLAYED_OPTIONS_COUNT = 3;
const transformCamelToSnakeCase = (key: string) => key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

type TListingListViewContentCars = {
  attributes: Attribute[];
  categories?: TCategories;
  correlationId?: string;
  date: string;
  fetchPriority?: 'auto' | 'high' | 'low';
  decodingType?: 'sync' | 'async' | 'auto';
  extendedAttributes: Attribute[];
  isFavorite?: boolean;
  loadingType?: 'eager' | 'lazy';
  location: { label: string; distance?: string };
  napAvailable?: boolean;
  onFavorite: () => void;
  opvalStickerText?: string;
  pageLocation?: string;
  pictures?: TImage[];
  price: string;
  priceType?: string;
  priorityProduct?: string;
  sellerInformation?: TSellerInformation;
  subtitle?: string;
  thinContent?: boolean;
  title: string;
  trackingData?: string;
  trustIndicators?: { value?: string; key: string }[];
  urgencyFeatureActive?: boolean;
  vipUrl: string;
  otherSellerListings?: TOtherSellerListings;
  otherSellerUrl?: string;
  itemId: string;
  shortTitle?: string;
  displayTitle?: string;
};
type TCarAttributes = { key: string; value: string; unit?: string; values?: string[]; icon: string }[];

const ListingListViewContentCars = ({
  attributes = [],
  categories,
  correlationId,
  date,
  decodingType,
  extendedAttributes = [],
  isFavorite = false,
  loadingType,
  fetchPriority,
  location,
  napAvailable = false,
  onFavorite,
  opvalStickerText,
  pageLocation,
  pictures,
  price,
  priceType,
  priorityProduct = '',
  sellerInformation,
  subtitle,
  thinContent,
  title,
  trackingData,
  trustIndicators = [],
  urgencyFeatureActive = false,
  vipUrl,
  itemId,
  otherSellerListings,
  otherSellerUrl,
  shortTitle,
  displayTitle,
}: TListingListViewContentCars) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const { t } = useI18nContext();
  const { labsConfig } = useContext(EnvironmentContext);
  const { isShortTitleEnabled } = experiments({ labsConfig });

  const handleAdClick = () => {
    trackEvent({
      eventAction: trackingKeys.CAR_AD_CLICKED.EVENT_ACTION,
      eventLabel:
        otherSellerListings && otherSellerListings.totalSellerListingsResults > 1
          ? trackingKeys.CAR_AD_CLICKED.EVENT_LABEL.GALLERY
          : trackingKeys.CAR_AD_CLICKED.EVENT_LABEL.REGULAR,
    });
  };

  let sellerLogoUrl;
  if (sellerInformation?.sellerLogoUrl) {
    sellerLogoUrl = imageSizeFormatter({
      imageUrlFromApi: sellerInformation.sellerLogoUrl,
      customSize: ImageSize.Medium,
    });
  }

  const options = attributes.find((x) => x.key === OPTIONS_ATTRIBUTE_KEY);
  const hasExtendedContent = !!options?.values?.length;
  let optionsValue = '';
  if (options?.values) {
    const remainingOptionsCount =
      options.values.length > DISPLAYED_OPTIONS_COUNT
        ? `, + ${options.values.length - DISPLAYED_OPTIONS_COUNT} ${t('listings.attributes.options')}`
        : '';
    optionsValue = `${options.values.slice(0, DISPLAYED_OPTIONS_COUNT).join(', ')}${remainingOptionsCount}`;
  }

  const carPassUrl = attributes.find((x) => x.key === CAR_PASS_ATTRIBUTE_KEY);

  const currencyPriceTypes = [PRICE_TYPE.MIN_BID, PRICE_TYPE.FIXED];
  const isCurrency = currencyPriceTypes.includes(priceType as PRICE_TYPE);
  const isPricePerMonth =
    isCurrency &&
    attributes.find(
      (attr) => attr.value === PRICE_TYPE_ATTRIBUTE_VALUE_KEY_NL || attr.value === PRICE_TYPE_ATTRIBUTE_VALUE_KEY_FR,
    );

  const carAttributes: TCarAttributes = [];
  const isEvCar = attributes?.find(
    ({ key, value }) => key === 'fuel' && (value === 'Elektrisch' || value === 'Électrique'),
  );
  const carAttributeKeys = isEvCar ? CAR_EV_ATTRIBUTES : CAR_ATTRIBUTES;
  carAttributeKeys.forEach((key) => {
    const listingAttribute = attributes?.find((x) => x.key === key);
    if (listingAttribute) {
      carAttributes.push({
        ...listingAttribute,
        icon: `car_${transformCamelToSnakeCase(key)}`,
      });
    }
  });
  const titleId = `Listing-title-${itemId}`;
  const displayedTitle = isShortTitleEnabled && shortTitle ? shortTitle : title;
  const displayedSubtitle = isShortTitleEnabled && displayTitle ? displayTitle : subtitle;

  return (
    <>
      <div className={Classes.wrapper}>
        <ListingVipLink
          vipUrl={vipUrl}
          trackingData={trackingData}
          correlationId={correlationId}
          pageLocation={pageLocation}
          thinContent={thinContent}
          onClick={handleAdClick}
          className={Classes.container}
        >
          <div className={Classes.image}>
            <ListingFigure
              images={imageFormatter(pictures ?? [], title, categories, extendedAttributes)}
              fetchPriority={fetchPriority}
              loadingType={loadingType}
              decodingType={decodingType}
              isFavorite={isFavorite}
              onFavorite={onFavorite}
              showFavoriteIcon={true}
              urgencyFeatureActive={urgencyFeatureActive}
              opvalStickerText={opvalStickerText}
              enableLandscapeContain={true}
            />
          </div>

          <div className={Classes.group}>
            <div className={Classes.headings}>
              <div className={Classes.titleContainer}>
                <TextLarge strong className={Classes.title} id={titleId}>
                  {displayedTitle}
                </TextLarge>

                {displayedSubtitle && (
                  <TextRegular className={Classes.subtitle} id={`${titleId}-subtitle`}>
                    {displayedSubtitle}
                  </TextRegular>
                )}
              </div>

              <div className={Classes.priceExtendedDetails}>
                {isPricePerMonth && <span>{`${t('listings.leaseprice.prefix')}`}&nbsp;</span>}
                <Title4>{price}</Title4>
                {isPricePerMonth && <span>&nbsp;{`${t('listings.leaseprice.suffix')}`}</span>}
                {hasExtendedContent && (
                  <ListingDetailsButton isDetailsOpen={isDetailsOpen} setIsDetailsOpen={setIsDetailsOpen} />
                )}
              </div>
            </div>

            {Boolean(carAttributes.length) && (
              <div className={Classes.attributes}>
                {carAttributes.map(({ key, value, unit, icon }) => (
                  <ListingAttribute type="default" key={key}>
                    <Icon className={Classes.attributesIcon} name={icon as TIcon} />
                    {formatAttributeBasedOnKey(key, value, undefined, undefined)}
                    {SPACE_SEPARATOR}
                    {unit && unit}
                    {key === MILEAGE_ATTRIBUTE_KEY && napAvailable && (
                      <span className={Classes.attributeCheckmark} title={t('listings.nap')}>
                        <Icon name="checkmark" />
                      </span>
                    )}
                    {key === MILEAGE_ATTRIBUTE_KEY && carPassUrl && (
                      <span className={Classes.attributeCheckmark} title={t('listings.carPass')}>
                        <Icon name="checkmark" />
                      </span>
                    )}
                  </ListingAttribute>
                ))}
              </div>
            )}

            {hasExtendedContent && (
              <details
                className={
                  isDetailsOpen ? `${Classes.extendedContent} ${Classes.extendedContentOpen}` : Classes.extendedContent
                }
                open
              >
                <summary />
                {optionsValue && <p className={Classes.attributeOptions}>{optionsValue}</p>}
              </details>
            )}

            {Boolean(trustIndicators?.length) && (
              <TextRegular strong className={Classes.highlights}>
                {trustIndicators?.map((trustIndicator) => (
                  <ListingTrustIndicator
                    trustIndicator={trustIndicator}
                    key={`${trustIndicator.key}.${trustIndicator.value}`}
                  />
                ))}
              </TextRegular>
            )}
          </div>
        </ListingVipLink>
        <div className={Classes.footer}>
          {sellerLogoUrl && (
            <img
              src={sellerLogoUrl}
              style={{ backgroundImage: `url(${sellerLogoUrl})` }}
              className={Classes.sellerImage}
              alt={t('seller.logo.alt')}
            />
          )}
          <div className={Classes.seller}>
            {sellerInformation && (
              <ListingSellerName link={sellerInformation.soiUrl} isVerifiedSeller={false} as={Classes.sellerName}>
                {sellerInformation.sellerName}
              </ListingSellerName>
            )}
            <div className={`${Classes.sellerLocation} u-colorTextSecondary`}>
              <span>{location.label}</span>
              {location.distance ? <span className={Classes.sellerDistance}>{`${location.distance}`}</span> : ''}
              {sellerInformation?.showWebsiteUrl && sellerInformation?.sellerWebsiteUrl && (
                <SellerExternalWebsite sellerWebsiteUrl={sellerInformation.sellerWebsiteUrl} />
              )}
            </div>
          </div>

          <div className={Classes.meta}>
            {priorityProduct !== 'NONE' && (
              <div className={`${Classes.priority} hz-Listing-priority--all-devices`}>
                <Trans tagName="span" i18nKey={`priorityProduct.${priorityProduct}`} />
              </div>
            )}
            <div className={`${Classes.listingDate} u-colorTextSecondary`}>{date}</div>
          </div>
        </div>
      </div>
      {otherSellerListings && otherSellerUrl && otherSellerListings.totalSellerListingsResults > 1 && (
        <ListingOtherSeller
          otherSellerListings={otherSellerListings}
          pageLocation={pageLocation}
          correlationId={correlationId}
          url={otherSellerUrl}
          className={Classes.otherSeller}
        />
      )}
    </>
  );
};

// todo: find which properties are really needs to be checked for preventing rerendering
function arePropsEqual(prevProps, nextProps) {
  return isEqual(prevProps, nextProps);
}

export default memo(ListingListViewContentCars, arePropsEqual);
